/**
 * Draft Center - Manages all communication drafts
 * Email drafts, Slack drafts, etc.
 * SAFETY: All drafts require manual approval before sending
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, MessageSquare, Send, Edit, Trash2, Clock, Check,
  AlertCircle, Filter, Search, X, Loader2, RefreshCw,
  ChevronRight, Archive, Eye
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  ready_for_review: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  approved: 'bg-green-500/20 text-green-400 border-green-500/30',
  sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  archived: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
};

const statusLabels = {
  draft: 'Draft',
  ready_for_review: 'Ready for Review',
  approved: 'Approved',
  sent: 'Sent',
  archived: 'Archived',
};

const typeIcons = {
  email: Mail,
  slack: MessageSquare,
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function DraftCenter({ userId, isOpen, onClose, onSendDraft }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: '', status: '' });
  const [search, setSearch] = useState('');
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  const fetchDrafts = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      let url = `${API}/api/email-ai/drafts?user_id=${userId}`;
      if (filter.type) url += `&type=${filter.type}`;
      if (filter.status) url += `&status=${filter.status}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setDrafts(data.drafts || []);
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
      toast.error('Failed to load drafts');
    } finally {
      setLoading(false);
    }
  }, [userId, filter]);

  useEffect(() => {
    if (isOpen) {
      fetchDrafts();
    }
  }, [isOpen, fetchDrafts]);

  const updateDraftStatus = async (draftId, newStatus) => {
    try {
      const res = await fetch(`${API}/api/email-ai/drafts/${draftId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (res.ok) {
        toast.success(`Draft ${newStatus === 'approved' ? 'approved' : 'updated'}`);
        fetchDrafts();
        if (selectedDraft?.id === draftId) {
          setSelectedDraft(prev => ({ ...prev, status: newStatus }));
        }
      }
    } catch (error) {
      toast.error('Failed to update draft');
    }
  };

  const updateDraftContent = async (draftId, content) => {
    try {
      const res = await fetch(`${API}/api/email-ai/drafts/${draftId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      
      if (res.ok) {
        toast.success('Draft updated');
        setEditMode(false);
        fetchDrafts();
        if (selectedDraft?.id === draftId) {
          setSelectedDraft(prev => ({ ...prev, content }));
        }
      }
    } catch (error) {
      toast.error('Failed to update draft');
    }
  };

  const deleteDraft = async (draftId) => {
    if (!confirm('Delete this draft?')) return;
    
    try {
      const res = await fetch(`${API}/api/email-ai/drafts/${draftId}`, {
        method: 'DELETE',
      });
      
      if (res.ok) {
        toast.success('Draft deleted');
        fetchDrafts();
        if (selectedDraft?.id === draftId) {
          setSelectedDraft(null);
        }
      }
    } catch (error) {
      toast.error('Failed to delete draft');
    }
  };

  const handleSend = async (draft) => {
    // This would trigger the actual send via the appropriate service
    // For now, we mark it as sent and call the callback
    if (draft.status !== 'approved') {
      toast.error('Please approve the draft before sending');
      return;
    }
    
    if (onSendDraft) {
      const success = await onSendDraft(draft);
      if (success) {
        await updateDraftStatus(draft.id, 'sent');
      }
    } else {
      // Mark as sent (actual sending would be implemented per channel)
      await updateDraftStatus(draft.id, 'sent');
      toast.success('Draft marked as sent');
    }
  };

  const filteredDrafts = drafts.filter(draft => {
    if (search) {
      const q = search.toLowerCase();
      return (
        draft.title?.toLowerCase().includes(q) ||
        draft.content?.toLowerCase().includes(q) ||
        draft.recipient?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const emailDrafts = filteredDrafts.filter(d => d.type === 'email');
  const slackDrafts = filteredDrafts.filter(d => d.type === 'slack');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/20 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-white/20 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Draft Center</h2>
            <p className="text-sm text-gray-400">Review and send your communication drafts</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchDrafts}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-white/10 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search drafts..."
              className="pl-9 bg-black border-white/20 text-white"
            />
          </div>
          <select
            value={filter.type}
            onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
            className="bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="">All Types</option>
            <option value="email">Email</option>
            <option value="slack">Slack</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            className="bg-black border border-white/20 rounded-lg px-3 py-2 text-sm text-white"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="ready_for_review">Ready for Review</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Drafts List */}
          <div className="w-1/2 border-r border-white/10 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-white" />
              </div>
            ) : filteredDrafts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                <Mail className="w-10 h-10 mb-2 opacity-50" />
                <p>No drafts yet</p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {/* Email Drafts Section */}
                {emailDrafts.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-white/5 text-sm text-gray-400 font-medium flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email Drafts ({emailDrafts.length})
                    </div>
                    {emailDrafts.map(draft => (
                      <DraftListItem
                        key={draft.id}
                        draft={draft}
                        selected={selectedDraft?.id === draft.id}
                        onClick={() => {
                          setSelectedDraft(draft);
                          setEditedContent(draft.content);
                          setEditMode(false);
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Slack Drafts Section */}
                {slackDrafts.length > 0 && (
                  <div>
                    <div className="px-4 py-2 bg-white/5 text-sm text-gray-400 font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Slack Drafts ({slackDrafts.length})
                    </div>
                    {slackDrafts.map(draft => (
                      <DraftListItem
                        key={draft.id}
                        draft={draft}
                        selected={selectedDraft?.id === draft.id}
                        onClick={() => {
                          setSelectedDraft(draft);
                          setEditedContent(draft.content);
                          setEditMode(false);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Draft Detail */}
          <div className="w-1/2 overflow-y-auto">
            {selectedDraft ? (
              <div className="p-4 space-y-4">
                {/* Draft Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">{selectedDraft.title}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[selectedDraft.status]}`}>
                        {statusLabels[selectedDraft.status]}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatDate(selectedDraft.created_at)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditMode(!editMode);
                        setEditedContent(selectedDraft.content);
                      }}
                      className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteDraft(selectedDraft.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg text-gray-400 hover:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Meta Info */}
                <div className="text-sm space-y-1">
                  {selectedDraft.type === 'email' && selectedDraft.recipient && (
                    <div className="text-gray-400">
                      <span className="text-gray-500">To:</span> {selectedDraft.recipient}
                    </div>
                  )}
                  {selectedDraft.type === 'email' && selectedDraft.subject && (
                    <div className="text-gray-400">
                      <span className="text-gray-500">Subject:</span> {selectedDraft.subject}
                    </div>
                  )}
                  {selectedDraft.type === 'slack' && selectedDraft.channel_name && (
                    <div className="text-gray-400">
                      <span className="text-gray-500">Channel:</span> #{selectedDraft.channel_name}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="border border-white/20 rounded-xl overflow-hidden">
                  {editMode ? (
                    <textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="w-full h-64 bg-black p-4 text-sm text-white resize-none focus:outline-none"
                    />
                  ) : (
                    <div className="p-4 bg-black/50 text-sm text-white whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedDraft.content}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  {editMode ? (
                    <>
                      <Button
                        onClick={() => updateDraftContent(selectedDraft.id, editedContent)}
                        className="bg-green-500 hover:bg-green-600 text-white"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button
                        onClick={() => {
                          setEditMode(false);
                          setEditedContent(selectedDraft.content);
                        }}
                        variant="outline"
                        className="border-white/20 text-white"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {selectedDraft.status === 'draft' && (
                        <Button
                          onClick={() => updateDraftStatus(selectedDraft.id, 'ready_for_review')}
                          variant="outline"
                          className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                          data-testid="draft-mark-ready-btn"
                        >
                          Mark Ready for Review
                        </Button>
                      )}
                      {selectedDraft.status === 'ready_for_review' && (
                        <Button
                          onClick={() => updateDraftStatus(selectedDraft.id, 'approved')}
                          className="bg-green-500 hover:bg-green-600 text-white"
                          data-testid="draft-approve-btn"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                      )}
                      {selectedDraft.status === 'approved' && (
                        <Button
                          onClick={() => handleSend(selectedDraft)}
                          className="bg-blue-500 hover:bg-blue-600 text-white"
                          data-testid="draft-send-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send Now
                        </Button>
                      )}
                      {selectedDraft.status !== 'archived' && selectedDraft.status !== 'sent' && (
                        <Button
                          onClick={() => updateDraftStatus(selectedDraft.id, 'archived')}
                          variant="outline"
                          className="border-white/20 text-gray-400 hover:bg-white/10"
                          data-testid="draft-archive-btn"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Archive
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {/* Safety Notice */}
                <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Safety Notice:</strong> This draft will not be sent automatically. 
                    You must manually approve and click &quot;Send Now&quot; to transmit this message.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Eye className="w-10 h-10 mb-2 opacity-50" />
                <p>Select a draft to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Draft List Item Component
function DraftListItem({ draft, selected, onClick }) {
  const Icon = typeIcons[draft.type] || Mail;
  
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 hover:bg-white/5 transition-colors flex items-start gap-3 ${selected ? 'bg-white/10' : ''}`}
    >
      <div className={`p-2 rounded-lg ${draft.type === 'slack' ? 'bg-purple-500/20' : 'bg-blue-500/20'}`}>
        <Icon className={`w-4 h-4 ${draft.type === 'slack' ? 'text-purple-400' : 'text-blue-400'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm text-white font-medium truncate">{draft.title}</span>
          <span className={`px-1.5 py-0.5 text-xs rounded border flex-shrink-0 ${statusColors[draft.status]}`}>
            {statusLabels[draft.status]}
          </span>
        </div>
        <p className="text-xs text-gray-400 truncate mt-0.5">
          {draft.content?.slice(0, 60)}...
        </p>
        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {formatDate(draft.created_at)}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
    </button>
  );
}
